import React from 'react';
import { 
    ArrowDownTrayIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline';

const CvPreview = ({ 
    cvData, 
    cvStyle, 
    template, 
    onBack, 
    onExport, 
    isLoading, 
    isEmbedded = false 
}) => {
    const getFontFamily = (fontName) => {
        const fontMap = {
            'Inter': 'Inter, sans-serif',
            'Poppins': 'Poppins, sans-serif',
            'Montserrat': 'Montserrat, sans-serif',
            'Roboto': 'Roboto, sans-serif',
            'Open Sans': 'Open Sans, sans-serif',
            'Bebas Neue': 'Bebas Neue, sans-serif'
        };
        return fontMap[fontName] || 'Inter, sans-serif';
    };

    const getFontSize = (size) => {
        const sizeMap = {
            'small': 'text-sm',
            'medium': 'text-base',
            'large': 'text-lg'
        };
        return sizeMap[size] || 'text-base';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString + '-01');
        return date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
    };

    const getSkillLevelColor = (level) => {
        const colors = {
            'beginner': 'bg-red-200 text-red-800',
            'intermediate': 'bg-yellow-200 text-yellow-800',
            'advanced': 'bg-blue-200 text-blue-800',
            'expert': 'bg-green-200 text-green-800'
        };
        return colors[level] || 'bg-gray-200 text-gray-800';
    };

    const getSkillLevelText = (level) => {
        const texts = {
            'beginner': 'Débutant',
            'intermediate': 'Intermédiaire',
            'advanced': 'Avancé',
            'expert': 'Expert'
        };
        return texts[level] || 'Intermédiaire';
    };

    const renderModernTemplate = () => (
        <div 
            className="bg-white shadow-2xl rounded-lg overflow-hidden"
            style={{ fontFamily: getFontFamily(cvStyle.fontFamily) }}
        >
            {/* Header */}
            <div 
                className="p-8 text-white"
                style={{ backgroundColor: cvStyle.primaryColor }}
            >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="break-words">
                        <h1 className="text-4xl font-bold mb-2">
                            {cvData.personalInfo.firstName} {cvData.personalInfo.lastName}
                        </h1>
                        <p className="text-xl opacity-90 mb-4">
                            {cvData.experience[0]?.title || 'Professionnel'}
                        </p>
                        <div className="space-y-1 text-sm opacity-80 break-words">
                            {cvData.personalInfo.email && (
                                <p className="break-all">📧 {cvData.personalInfo.email}</p>
                            )}
                            {cvData.personalInfo.phone && (
                                <p className="break-all">📱 {cvData.personalInfo.phone}</p>
                            )}
                            {cvData.personalInfo.address && (
                                <p className="break-words">📍 {cvData.personalInfo.address}</p>
                            )}
                        </div>
                    </div>
                    <div className="text-left md:text-right space-y-1 text-sm opacity-80 break-all">
                        {cvData.personalInfo.linkedin && (
                            <p>💼 LinkedIn</p>
                        )}
                        {cvData.personalInfo.website && (
                            <p>🌐 Site web</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-8">
                {/* Summary */}
                {cvData.summary && (
                    <div className="mb-8">
                        <h2 
                            className="text-2xl font-bold mb-4"
                            style={{ color: cvStyle.primaryColor }}
                        >
                            Résumé professionnel
                        </h2>
                        <p className={`${getFontSize(cvStyle.fontSize)} text-gray-700 leading-relaxed break-words`}>
                            {cvData.summary}
                        </p>
                    </div>
                )}

                {/* Experience */}
                {cvData.experience.length > 0 && (
                    <div className="mb-8">
                        <h2 
                            className="text-2xl font-bold mb-6"
                            style={{ color: cvStyle.primaryColor }}
                        >
                            Expérience professionnelle
                        </h2>
                        <div className="space-y-6">
                            {cvData.experience.map((exp, index) => (
                                <div key={exp.id} className="border-l-4 pl-6" style={{ borderColor: cvStyle.secondaryColor }}>
                                    <div className="flex items-start justify-between mb-2">
                                        <h3 className="text-xl font-semibold text-gray-800">
                                            {exp.title}
                                        </h3>
                                        <span className="text-sm text-gray-500">
                                            {formatDate(exp.startDate)} - {exp.current ? 'Présent' : formatDate(exp.endDate)}
                                        </span>
                                    </div>
                                    <p className="text-lg font-medium text-gray-600 mb-1">
                                        {exp.company}
                                    </p>
                                    {exp.location && (
                                        <p className="text-sm text-gray-500 mb-3">
                                            📍 {exp.location}
                                        </p>
                                    )}
                                    {exp.description && (
                                        <p className={`${getFontSize(cvStyle.fontSize)} text-gray-700 leading-relaxed break-words`}>
                                            {exp.description}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Education */}
                {cvData.education.length > 0 && (
                    <div className="mb-8">
                        <h2 
                            className="text-2xl font-bold mb-6"
                            style={{ color: cvStyle.primaryColor }}
                        >
                            Formation
                        </h2>
                        <div className="space-y-4">
                            {cvData.education.map((edu) => (
                                <div key={edu.id} className="border-l-4 pl-6" style={{ borderColor: cvStyle.secondaryColor }}>
                                    <div className="flex items-start justify-between mb-2">
                                        <h3 className="text-xl font-semibold text-gray-800">
                                            {edu.degree}
                                        </h3>
                                        <span className="text-sm text-gray-500">
                                            {formatDate(edu.startDate)} - {edu.current ? 'En cours' : formatDate(edu.endDate)}
                                        </span>
                                    </div>
                                    <p className="text-lg font-medium text-gray-600 mb-1">
                                        {edu.institution}
                                    </p>
                                    {edu.location && (
                                        <p className="text-sm text-gray-500 mb-2">
                                            📍 {edu.location}
                                        </p>
                                    )}
                                    {edu.description && (
                                        <p className={`${getFontSize(cvStyle.fontSize)} text-gray-700 leading-relaxed break-words`}>
                                            {edu.description}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Skills */}
                {cvData.skills.length > 0 && (
                    <div className="mb-8">
                        <h2 
                            className="text-2xl font-bold mb-6"
                            style={{ color: cvStyle.primaryColor }}
                        >
                            Compétences
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {cvData.skills.map((skill) => (
                                <div key={skill.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <span className="font-medium text-gray-800">
                                        {skill.name}
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSkillLevelColor(skill.level)}`}>
                                        {getSkillLevelText(skill.level)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderElegantTemplate = () => (
        <div 
            className="bg-gray-900 text-white shadow-2xl rounded-lg overflow-hidden"
            style={{ fontFamily: getFontFamily(cvStyle.fontFamily) }}
        >
            {/* Header */}
            <div className="p-8 border-b border-gray-700">
                <h1 className="text-5xl font-bold mb-4 text-center">
                    {cvData.personalInfo.firstName} {cvData.personalInfo.lastName}
                </h1>
                <p className="text-2xl text-center text-gray-300 mb-6">
                    {cvData.experience[0]?.title || 'Professionnel'}
                </p>
                <div className="flex flex-wrap justify-center gap-2 text-sm text-gray-400">
                    {cvData.personalInfo.email && (
                        <span className="break-all">📧 {cvData.personalInfo.email}</span>
                    )}
                    {cvData.personalInfo.phone && (
                        <span className="break-all">📱 {cvData.personalInfo.phone}</span>
                    )}
                    {cvData.personalInfo.address && (
                        <span className="break-words">📍 {cvData.personalInfo.address}</span>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="p-8">
                {/* Summary */}
                {cvData.summary && (
                    <div className="mb-8 text-center">
                        <p className={`${getFontSize(cvStyle.fontSize)} text-gray-300 leading-relaxed max-w-3xl mx-auto break-words`}>
                            {cvData.summary}
                        </p>
                    </div>
                )}

                {/* Experience */}
                {cvData.experience.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold mb-6 text-center border-b border-gray-700 pb-2">
                            Expérience professionnelle
                        </h2>
                        <div className="space-y-8">
                            {cvData.experience.map((exp) => (
                                <div key={exp.id} className="text-center">
                                    <h3 className="text-2xl font-semibold mb-2">
                                        {exp.title}
                                    </h3>
                                    <p className="text-xl text-gray-300 mb-1">
                                        {exp.company}
                                    </p>
                                    <p className="text-sm text-gray-500 mb-4">
                                        {formatDate(exp.startDate)} - {exp.current ? 'Présent' : formatDate(exp.endDate)}
                                    </p>
                                    {exp.description && (
                                        <p className={`${getFontSize(cvStyle.fontSize)} text-gray-300 leading-relaxed max-w-2xl mx-auto break-words`}>
                                            {exp.description}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Skills */}
                {cvData.skills.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold mb-6 text-center border-b border-gray-700 pb-2">
                            Compétences
                        </h2>
                        <div className="flex flex-wrap justify-center gap-4">
                            {cvData.skills.map((skill) => (
                                <div key={skill.id} className="px-4 py-2 border border-gray-600 rounded-full">
                                    <span className="font-medium">{skill.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderCreativeTemplate = () => (
        <div 
            className="bg-gradient-to-br from-purple-400 via-pink-400 to-purple-600 text-white shadow-2xl rounded-lg overflow-hidden"
            style={{ fontFamily: getFontFamily(cvStyle.fontFamily) }}
        >
            {/* Header */}
            <div className="p-8 text-center">
                <div className="w-32 h-32 bg-white rounded-full mx-auto mb-6 flex items-center justify-center">
                    <span className="text-4xl font-bold text-purple-600">
                        {cvData.personalInfo.firstName?.[0]}{cvData.personalInfo.lastName?.[0]}
                    </span>
                </div>
                <h1 className="text-5xl font-bold mb-4">
                    {cvData.personalInfo.firstName} {cvData.personalInfo.lastName}
                </h1>
                <p className="text-2xl opacity-90 mb-6">
                    {cvData.experience[0]?.title || 'Professionnel'}
                </p>
                <div className="flex flex-wrap justify-center gap-2 text-sm">
                    {cvData.personalInfo.email && (
                        <span className="break-all">📧 {cvData.personalInfo.email}</span>
                    )}
                    {cvData.personalInfo.phone && (
                        <span className="break-all">📱 {cvData.personalInfo.phone}</span>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="bg-white text-gray-800 p-8">
                {/* Summary */}
                {cvData.summary && (
                    <div className="mb-8 text-center">
                        <p className={`${getFontSize(cvStyle.fontSize)} text-gray-600 leading-relaxed max-w-3xl mx-auto`}>
                            {cvData.summary}
                        </p>
                    </div>
                )}

                {/* Experience */}
                {cvData.experience.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold mb-6 text-center text-purple-600">
                            Expérience professionnelle
                        </h2>
                        <div className="space-y-6">
                            {cvData.experience.map((exp) => (
                                <div key={exp.id} className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl">
                                    <h3 className="text-2xl font-semibold mb-2 text-purple-700">
                                        {exp.title}
                                    </h3>
                        <p className="text-xl text-gray-600 mb-2 break-words">
                                        {exp.company}
                                    </p>
                                    <p className="text-sm text-gray-500 mb-3">
                                        {formatDate(exp.startDate)} - {exp.current ? 'Présent' : formatDate(exp.endDate)}
                                    </p>
                                    {exp.description && (
                                        <p className={`${getFontSize(cvStyle.fontSize)} text-gray-700 leading-relaxed break-words`}>
                                            {exp.description}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderTemplate = () => {
        switch (template?.id) {
            case 'elegant-dark':
                return renderElegantTemplate();
            case 'creative-gradient':
                return renderCreativeTemplate();
            default:
                return renderModernTemplate();
        }
    };

    if (isEmbedded) {
        return (
            <div className="h-full overflow-y-auto bg-gray-100 p-4">
                <div className="max-w-4xl mx-auto">
                    {renderTemplate()}
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 overflow-x-hidden">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-8">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Aperçu de votre CV</h2>
                    <p className="text-gray-600">Template : {template?.name}</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <button
                        onClick={onBack}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        ← Retour
                    </button>
                    <button
                        onClick={() => onExport('pdf')}
                        disabled={isLoading}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Export...
                            </>
                        ) : (
                            <>
                                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                                Exporter PDF
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Preview */}
            <div className="bg-gray-100 p-4 md:p-8 rounded-2xl overflow-x-auto min-w-0">
                <div className="max-w-4xl mx-auto w-full min-w-0">
                    {renderTemplate()}
                </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex justify-center space-x-4">
                <button
                    onClick={() => onExport('pdf')}
                    disabled={isLoading}
                    className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                    <DocumentTextIcon className="w-5 h-5 mr-2" />
                    Exporter PDF
                </button>
                <button
                    onClick={() => onExport('docx')}
                    disabled={isLoading}
                    className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                    <DocumentTextIcon className="w-5 h-5 mr-2" />
                    Exporter Word
                </button>
            </div>
        </div>
    );
};

export default CvPreview; 